import {Box, Button, CircularProgress, Paper} from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import Form, {Actions, BasicInput} from "view/components/Form";
import Typography from "@material-ui/core/Typography";
import {TwoFactorMethodType} from "@evennt-tech/api-v2-external";
import TwoFactorChallenge from "view/components/TwoFactorChallenge";
import {activateUser, apiV2, signInWithCustomToken} from "api/firebase";
import {impersonationStore} from "tools/auth/impersonationStore";
import React, {useEffect, useRef, useState} from "react";
import {T, useTranslate} from "@tolgee/react";
import {useForm} from "react-hook-form";
import {Link, useNavigate} from "react-router-dom";
import {AxiosError} from "axios";
import Copyright from "view/components/Copyright";
import GoogleSignInButton from "view/components/GoogleSignInButton";
import PublicLanguagePicker from "view/components/LanguagePicker/PublicLanguagePicker";
import Alert from "view/components/Alert";
import KeyIcon from "view/images/KeyIcon";
import LoginIcon from "view/images/LoginIcon";
import {ADMIN_LOGIN_PATH, APP_PATH, FORGOT_PASSWORD} from "view/routes";
import theme from "tools/theme";
import {useSelector} from "react-redux";
import {selectUser} from "store/auth/selectors";
import {useVersion} from "store/version/version.store";
import {
  cancelSessionSwitch,
  markSessionSwitchInProgress,
} from "tools/auth/sessionSync";
import {
  consumeGoogleAccountLinked,
  GoogleCallbackStatus,
  markGoogleAccountLinked,
  readGoogleCallbackResult,
  resolveGoogleErrorKey,
  startGoogleSignIn,
} from "tools/auth/googleSignIn";
import {
  LastLoginMethod,
  readLastLoginMethod,
  writeLastLoginMethod,
} from "tools/auth/lastLoginMethod";
import {getRedirectPathAfterLogin, quickOrderIdParamName} from "tools/routing";
import {logoutUser, prepareSessionSwitch} from "tools/auth/logoutCleanup";
import {useLangParamPreselect} from "hooks/useLangParamPreselect";
import useStyles from "./style";

interface FormData {
  password: string;
  email: string;
}

const Login: React.FC = () => {
  const classes = useStyles();
  const {t} = useTranslate();
  const history = useNavigate();
  const {id} = useSelector(selectUser);

  useLangParamPreselect();

  const [loginError, setLoginError] = useState(false);
  const [isAdminGoogleRequired, setIsAdminGoogleRequired] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(
    () => !!impersonationStore.get()?.token
  );
  const [impersonationError, setImpersonationError] = useState(false);
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [twoFactorMethods, setTwoFactorMethods] = useState<
    TwoFactorMethodType[]
  >([]);
  const [activeMethod, setActiveMethod] = useState<TwoFactorMethodType | null>(
    null
  );
  const [googleErrorKey, setGoogleErrorKey] = useState<string | null>(null);
  const [isGoogleBusy, setIsGoogleBusy] = useState(false);
  // Read once on mount so the hint is on screen straight away, with no hover,
  // focus or click, and without asking the server anything.
  const [lastLoginMethod] = useState<LastLoginMethod | null>(
    readLastLoginMethod
  );

  // Which way in is in flight. `TwoFactorChallenge` hands back only a custom
  // token, so the method has to be remembered across the challenge to be
  // recorded once the session actually starts.
  const signInMethodRef = useRef<LastLoginMethod>(LastLoginMethod.Password);
  const isGoogleLinkedRef = useRef(false);
  const isHandlingGoogleCallbackRef = useRef(false);

  const params = new URLSearchParams(window.location.search);
  const showId = params.get("showId");
  const redirectPath = getRedirectPathAfterLogin(window.location.search);

  const form = useForm<FormData>({
    mode: "all",
  });
  const {
    handleSubmit,
    setError,
    clearErrors,
    formState: {isSubmitting},
    watch,
  } = form;
  const {email, password} = watch();

  const {autoCatch, fetch} = useVersion();

  useEffect(() => {
    clearErrors();
    setLoginError(false);
    setIsAdminGoogleRequired(false);
  }, [email, password, clearErrors]);

  useEffect(() => {
    if (id) {
      const path = getRedirectPathAfterLogin(window.location.search);

      history(path || APP_PATH);
    }
  }, [id, history]);

  useEffect(() => {
    const entry = impersonationStore.get();
    if (!entry) return;

    const autoLogin = async () => {
      impersonationStore.clear();
      try {
        const {customToken} = await apiV2.auth.loginWithImpersonationToken({
          impersonationToken: entry.token,
        });
        if (entry.meta?.isOrganiser) {
          localStorage.setItem("ORGANISER_FROM_ADMIN", "true");
        }
        markSessionSwitchInProgress();
        await signInWithCustomToken(customToken);
      } catch {
        cancelSessionSwitch();
        setIsImpersonating(false);
        setImpersonationError(true);
      }
    };

    autoLogin();
  }, []);

  const completeLogin = async (customToken: string) => {
    // Parked before the session starts: the app shell mounts on the auth
    // change and reads the flag once, before the calls below return.
    if (isGoogleLinkedRef.current) markGoogleAccountLinked();

    markSessionSwitchInProgress();
    try {
      await signInWithCustomToken(customToken);
    } catch (error) {
      // No session, so no portal page to show it on.
      consumeGoogleAccountLinked();
      throw error;
    }
    await apiV2.user.getMe();
    await apiV2.notification.connect();

    // The session has started, so this is the sign-in the hint should describe.
    // A failed attempt or an abandoned two-factor step never reaches here.
    writeLastLoginMethod(signInMethodRef.current);
    isGoogleLinkedRef.current = false;

    if (showId) {
      activateUser(showId);
    }
  };

  const resetTwoFactor = () => {
    setPendingToken(null);
    setTwoFactorMethods([]);
    setActiveMethod(null);
  };

  useEffect(() => {
    const result = readGoogleCallbackResult();

    if (!result || isHandlingGoogleCallbackRef.current) return;

    isHandlingGoogleCallbackRef.current = true;
    signInMethodRef.current = LastLoginMethod.Google;

    if (result.status === GoogleCallbackStatus.TwoFactor) {
      isGoogleLinkedRef.current = result.linked;
      setPendingToken(result.pendingToken);
      setTwoFactorMethods(result.methods);
      setActiveMethod(result.method);
      isHandlingGoogleCallbackRef.current = false;
      return;
    }

    if (result.status === GoogleCallbackStatus.Success) {
      const {customToken, linked} = result;

      const finishGoogleSignIn = async () => {
        setIsGoogleBusy(true);
        isGoogleLinkedRef.current = linked;

        try {
          await completeLogin(customToken);
        } catch (error) {
          // The OAuth round trip succeeded but the custom token did not —
          // release sibling /login tabs from their loader instead of leaving
          // them waiting out the switch TTL.
          cancelSessionSwitch();
          console.error(error);
          setGoogleErrorKey(resolveGoogleErrorKey(null));
          setIsGoogleBusy(false);
          isHandlingGoogleCallbackRef.current = false;
        }
      };

      finishGoogleSignIn();
      return;
    }

    // Identity mode belongs to the embedded show sign-up, which never returns
    // here. Treated as a generic failure rather than left to fall through.
    if (result.status === GoogleCallbackStatus.Identity) {
      setGoogleErrorKey(resolveGoogleErrorKey(null));
      isHandlingGoogleCallbackRef.current = false;
      return;
    }

    setGoogleErrorKey(resolveGoogleErrorKey(result.reason));
    isHandlingGoogleCallbackRef.current = false;
    // Runs once per mount: the fragment is consumed and stripped inside
    // `readGoogleCallbackResult`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGoogleSignIn = async () => {
    try {
      setGoogleErrorKey(null);
      setIsGoogleBusy(true);
      await startGoogleSignIn("/login");
    } catch (error) {
      console.error(error);
      setGoogleErrorKey(resolveGoogleErrorKey(null));
      setIsGoogleBusy(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    const {password, email} = data;

    try {
      signInMethodRef.current = LastLoginMethod.Password;
      setGoogleErrorKey(null);

      const result = await apiV2.auth.logIn({
        email,
        password,
      });

      if ("twoFactorRequired" in result) {
        setPendingToken(result.pendingToken);
        setTwoFactorMethods(result.methods);
        setActiveMethod(result.method);
        return;
      }

      await completeLogin(result.customToken);
    } catch (e) {
      const axiosError = e as AxiosError<{
        message?: string;
        error?: string;
        statusCode?: number;
      }>;
      const isForbiddenAdminGoogleError =
        axiosError.response?.status === 403 &&
        axiosError.response?.data?.message ===
          "Admins must sign in with Google";

      if (isForbiddenAdminGoogleError) {
        await prepareSessionSwitch();
        setIsAdminGoogleRequired(true);
        return;
      }

      await logoutUser({
        reason: "login-error",
        redirect: false,
        broadcast: false,
      });
      // Notify sibling /login tabs that the in-flight switch is over so
      // they drop the loader instead of waiting out the 30s TTL.
      cancelSessionSwitch();

      setError("email", {
        type: "manual",
        message: t("global.login.user_not_found"),
      });
      setError("password", {
        type: "manual",
        message: t("global.login.wrong_password"),
      });
      setLoginError(true);
    } finally {
      await autoCatch();
      await fetch();
    }
  };

  if (isImpersonating || isGoogleBusy) {
    return (
      <Box
        className={classes.content}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (pendingToken && activeMethod) {
    return (
      <TwoFactorChallenge
        pendingToken={pendingToken}
        method={activeMethod}
        methods={twoFactorMethods}
        onComplete={completeLogin}
        onCancel={resetTwoFactor}
      />
    );
  }

  const renderLoginButtonIcon = () => {
    if (isSubmitting) {
      return <CircularProgress size={18} color="inherit" />;
    }
    return <LoginIcon fill="currentColor" />;
  };

  return (
    <>
      <Box className={classes.content}>
        <Box className={classes.container}>
          <Paper elevation={0} className={classes.paper}>
            <CssBaseline />
            <KeyIcon />
            <Typography className={classes.signInText}>
              <T keyName="global.login.sign_in" />
            </Typography>
            {impersonationError && (
              <Box mb={3} mt={-1} width="100%">
                <Alert severity="error">
                  <T keyName="global.login.impersonation_expired" />
                </Alert>
              </Box>
            )}
            <Form
              form={form}
              onSubmit={handleSubmit(onSubmit)}
              className={classes.form}
            >
              {redirectPath?.includes(quickOrderIdParamName) && (
                <Box mb={3} mt={-1} width="100%">
                  <Alert severity="info">
                    <T keyName="global.login.quick_order_login_required" />
                  </Alert>
                </Box>
              )}
              <BasicInput
                required
                name="email"
                label={t("common.field.email")}
                className={classes.field}
                rules={{
                  pattern: {
                    value: /\S+@\S+.\S+/,
                    message: t("common.validation.email"),
                  },
                }}
                inputProps={{autocomplete: "off"}}
              />
              <BasicInput
                required
                name="password"
                label={t("common.field.password")}
                type="password"
                className={classes.field}
              />
              {loginError && (
                <Alert severity="error" className={classes.errorText}>
                  <T keyName="global.login.credentials_incorrect" />
                </Alert>
              )}
              {googleErrorKey && (
                <Box mb={2} width="100%">
                  <Alert severity="error" className={classes.errorText}>
                    <T keyName={googleErrorKey} />
                  </Alert>
                </Box>
              )}
              {isAdminGoogleRequired && (
                <Box mb={2} width="100%">
                  <Alert severity="error" className={classes.errorText}>
                    <T keyName="global.login.admin_google_required" />
                  </Alert>
                </Box>
              )}
              <Actions className={classes.actions}>
                <Link to={FORGOT_PASSWORD} className={classes.signInLink}>
                  <T keyName="global.login.forgot_password" />
                </Link>
                <Button
                  type="submit"
                  variant="contained"
                  endIcon={renderLoginButtonIcon()}
                  className={classes.submit}
                  disabled={
                    loginError || isAdminGoogleRequired || !email || !password
                  }
                >
                  <T keyName="global.login.login_button" />
                </Button>
              </Actions>
              <GoogleSignInButton
                withDivider
                onClick={onGoogleSignIn}
                labelKey="global.login.sign_in_with_google"
                isLastLogin={lastLoginMethod === LastLoginMethod.Google}
              />
            </Form>
          </Paper>
        </Box>
        <Box>
          <PublicLanguagePicker />
        </Box>
        <Box>
          <Copyright />
        </Box>
      </Box>
    </>
  );
};

export default Login;
